/**
 * ThreatOpportunityCard — Threats, opportunities, and gap actions.
 *
 * Two-column layout (stacks on mobile) showing evolving deal
 * intelligence as the conversation progresses.
 */

import { GAP_QUESTIONS } from "./DealIntelCard";

interface ThreatOpportunityCardProps {
  data: {
    threats: string[];
    opportunities: string[];
    gaps: string[];
    gap_questions: Array<{ gap: string; question: string }>;
  };
  onFollowUp?: (message: string) => void;
}

export default function ThreatOpportunityCard({
  data,
  onFollowUp,
}: ThreatOpportunityCardProps) {
  const { threats, opportunities, gaps, gap_questions } = data;

  // Prefer pre-mapped gap_questions; fall back to local lookup
  const gapActions: Array<{ gap: string; question: string }> =
    gap_questions.length > 0
      ? gap_questions
      : gaps
          .map((g) => ({ gap: g, question: GAP_QUESTIONS[g] ?? "" }))
          .filter((gq) => gq.question !== "");

  return (
    <div className="to-card">
      <div className="to-columns">
        {/* Threats */}
        <div className="to-col to-col--threats">
          <div className="to-col-header">Threats</div>
          {threats.length > 0 ? (
            threats.map((t, i) => (
              <div key={i} className="to-row">
                <span className="to-icon to-icon--threat">&#9888;</span>
                <span>{t}</span>
              </div>
            ))
          ) : (
            <div className="to-empty">No threats identified</div>
          )}
        </div>

        {/* Opportunities */}
        <div className="to-col to-col--opps">
          <div className="to-col-header">Opportunities</div>
          {opportunities.length > 0 ? (
            opportunities.map((o, i) => (
              <div key={i} className="to-row">
                <span className="to-icon to-icon--opp">&#8599;</span>
                <span>{o}</span>
              </div>
            ))
          ) : (
            <div className="to-empty">No opportunities identified</div>
          )}
        </div>
      </div>

      {/* Gap footer */}
      {gapActions.length > 0 && onFollowUp && (() => {
        const sendFollowUp = onFollowUp;
        return (
          <div className="to-gap-footer">
            <span className="to-gap-count">{gaps.length} gap{gaps.length !== 1 ? "s" : ""} remaining</span>
            <div className="to-chips">
              {gapActions.map(({ gap, question }) => (
                <button
                  key={gap}
                  className="to-chip"
                  onClick={() => sendFollowUp(question)}
                >
                  {gap.replace(/ not completed| data missing| data incomplete/g, "")}
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      <style>{toStyles}</style>
    </div>
  );
}

const toStyles = `
  .to-card {
    background: var(--surface, #111827);
    border: 1px solid var(--border, #1e293b);
    border-radius: 12px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .to-columns {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  .to-col {
    border-radius: 8px;
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .to-col--threats {
    background: rgba(239, 68, 68, 0.06);
    border-left: 3px solid var(--error, #ef4444);
  }
  .to-col--opps {
    background: rgba(16, 185, 129, 0.06);
    border-left: 3px solid var(--success, #10b981);
  }
  .to-col-header {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-dim, #64748b);
    margin-bottom: 2px;
  }
  .to-row {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    font-size: 13px;
    color: var(--text-muted, #94a3b8);
  }
  .to-icon { flex-shrink: 0; }
  .to-icon--threat { color: var(--error, #ef4444); }
  .to-icon--opp { color: var(--success, #10b981); }
  .to-empty {
    font-size: 12px;
    color: var(--text-dim, #64748b);
    font-style: italic;
  }
  .to-gap-footer {
    padding-top: 4px;
    border-top: 1px solid var(--border, #1e293b);
  }
  .to-gap-count {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-muted, #94a3b8);
    display: block;
    margin-bottom: 6px;
  }
  .to-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .to-chip {
    font-size: 12px;
    padding: 4px 10px;
    border-radius: 16px;
    border: 1px solid var(--border, #1e293b);
    background: var(--bg, #0c0f14);
    color: var(--accent, #3b82f6);
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
  }
  .to-chip:hover {
    background: var(--accent-glow, rgba(59,130,246,0.15));
    border-color: var(--accent, #3b82f6);
  }
  @media (max-width: 640px) {
    .to-columns {
      grid-template-columns: 1fr;
    }
  }
`;
